import {
  useEffect,
  useId,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { DomainCard } from "./DomainCard";
import { IconTile } from "./IconTile";
import type { Domain } from "./domains";

export interface ContentNavigationItem {
  href: string;
  label: string;
  icon?: LucideIcon;
  domain?: Domain;
  description?: string;
  badge?: string;
  date?: { value: string; label: string };
  current?: "page" | "location";
  children?: ContentNavigationItem[];
  defaultExpanded?: boolean;
}

export interface ContentNavigationProps {
  title: string;
  items: ContentNavigationItem[];
  className?: string;
  header?: ReactNode;
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
}

/** Brand 2.2 navigation for public content: one tree, with a compact mobile disclosure. */
export function ContentNavigation({
  title,
  items,
  className,
  onNavigate,
  header,
}: ContentNavigationProps) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const [groups, setGroups] = useState<Record<string, boolean>>({});
  const selectedKey = JSON.stringify(
    items
      .flatMap((item) => [item, ...(item.children ?? [])])
      .filter((item) => item.current)
      .map((item) => item.href),
  );
  useEffect(() => setGroups({}), [selectedKey]);
  const containsCurrent = (item: ContentNavigationItem): boolean =>
    Boolean(item.current || item.children?.some(containsCurrent));
  const list = (entries: ContentNavigationItem[], nested = false) => (
    <ul
      className={
        nested
          ? "ml-4 space-y-1 border-l border-caire-hairline pl-3"
          : "space-y-1"
      }
    >
      {entries.map((item, index) => {
        const hasChildren = Boolean(item.children?.length);
        const open =
          groups[item.href] ?? (item.defaultExpanded || containsCurrent(item));
        const controlId = `${id}-${nested ? "child" : "group"}-${index}`;
        const rowClass = `flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm leading-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caire-action ${containsCurrent(item) ? "bg-domain-planning-tint font-semibold text-domain-planning-ink" : "text-caire-muted hover:bg-caire-page hover:text-caire-ink"}`;
        const label = (
          <>
            {item.icon ? (
              <IconTile
                icon={item.icon}
                domain={item.domain ?? "society"}
                size="xxs"
                variant="tint"
              />
            ) : null}
            <span className="min-w-0 flex-1 break-words">
              <span className="block">{item.label}</span>
              {item.description ? (
                <span className="mt-1 block text-xs font-normal leading-5 text-caire-muted">
                  {item.description}
                </span>
              ) : null}
              {item.badge || item.date ? (
                <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal leading-5 text-caire-muted">
                  {item.badge ? (
                    <span className="rounded-md border border-caire-hairline bg-caire-page px-1.5 py-0.5 text-caire-ink">
                      {item.badge}
                    </span>
                  ) : null}
                  {item.date ? (
                    <time dateTime={item.date.value}>{item.date.label}</time>
                  ) : null}
                </span>
              ) : null}
            </span>
            {hasChildren ? (
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 shrink-0 ${open ? "rotate-180" : ""}`}
              />
            ) : null}
          </>
        );
        return (
          <li key={item.href}>
            {hasChildren ? (
              <button
                type="button"
                aria-expanded={Boolean(open)}
                aria-controls={controlId}
                className={rowClass}
                onClick={() =>
                  setGroups((previous) => ({ ...previous, [item.href]: !open }))
                }
              >
                {label}
              </button>
            ) : (
              <a
                href={item.href}
                aria-current={item.current}
                onClick={(event) => onNavigate?.(event, item.href)}
                className={rowClass}
              >
                {label}
              </a>
            )}
            {hasChildren ? (
              <div id={controlId} hidden={!open}>
                {list(item.children!, true)}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
  return (
    <nav aria-label={title} className={className}>
      <DomainCard domain="society" surface="white" className="p-2">
        <p className="hidden px-3 pb-3 pt-2 text-xs font-semibold uppercase tracking-widest text-caire-action lg:block">
          {title}
        </p>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded(!expanded)}
          className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-caire-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caire-action lg:hidden"
        >
          {title}
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
        <div
          id={id}
          className={`${expanded ? "block" : "hidden"} max-h-[65dvh] overflow-y-auto lg:block lg:max-h-none lg:overflow-visible`}
        >
          {header}
          {list(items)}
        </div>
      </DomainCard>
    </nav>
  );
}

/** Keeps a stack of content menus within the available desktop viewport. */
export function ContentNavigationGroup({ children }: { children: ReactNode }) {
  return (
    <aside className="space-y-4 lg:sticky lg:top-24 lg:max-h-[calc(100dvh-7rem)] lg:self-start lg:overflow-y-auto">
      {children}
    </aside>
  );
}
